import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import "../supplier/supplierManagementTables.css";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import {
  primaryBtn,
  secondaryBtn,
  tableCardSurface,
  thStyle,
} from "./assessmentStyles";
import AssessmentFilterBar from "./AssessmentFilterBar";
import AssessmentEmptyState from "./AssessmentEmptyState";
import { applyAssessmentFilters } from "./assessmentFilters";

const ACTIVE_STATUSES = ["ASSIGNED", "IN_PROGRESS"];
const PAST_STATUSES = ["SUBMITTED", "REVIEWED", "CANCELLED"];

/**
 * Supplier-side per-company assignments page. Shared component for both
 * /assessment/by-company/:companyId/active and /past — mode prop picks which
 * status bucket to render. The other one is offered as a button.
 */
const MyAssignmentsByCompany = ({ mode }) => {
  const { companyId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const statuses = mode === "past" ? PAST_STATUSES : ACTIVE_STATUSES;
  const title =
    mode === "past" ? "Past Assessments" : "Active Assessments";
  const otherMode = mode === "past" ? "active" : "past";
  const otherLabel =
    mode === "past" ? "Show Active" : "Show Past";

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await AssessmentService.listMyAssignments({
        company_id: Number(companyId),
        limit: 200,
      });
      setLoading(false);
      if (!res?.isSuccess) return;
      const items = res?.data?.data?.items ?? [];
      setRows(items.filter((r) => statuses.includes(r.status_key)));
      const inviter = items.find((r) => r.company_name)?.company_name;
      if (inviter) setCompanyName(inviter);
    })();
  }, [companyId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const headerTitle = companyName || `Company #${companyId}`;

  const filtered = useMemo(
    () =>
      applyAssessmentFilters(rows, {
        search,
        dateRange,
        dateField: "assigned_at",
      }),
    [rows, search, dateRange]
  );

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle={`${headerTitle} — ${title}`}
      totalCount={filtered.length}
      actionsSlot={
        <>
          <button
            type="button"
            style={secondaryBtn}
            onClick={() => history.push("/assessment/by-company")}
          >
            ← Back
          </button>
          <button
            type="button"
            style={primaryBtn}
            onClick={() =>
              history.push(
                `/assessment/by-company/${companyId}/${otherMode}`
              )
            }
          >
            {otherLabel}
          </button>
        </>
      }
    >
      <AssessmentFilterBar
        rows={rows}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search assessment…"
        showStatus={false}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        dateLabel="Assigned"
      />

      <div className="supplier-mgmt-table-wrap" style={tableCardSurface}>
        <div className="supplier-mgmt-table-scroll">
          <table className="supplier-mgmt-table table">
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 50 }}>#</th>
                <th style={thStyle}>Assessment</th>
                <th style={{ ...thStyle, width: 120 }}>Assigned</th>
                <th style={{ ...thStyle, width: 120 }}>Due</th>
                <th
                  style={{ ...thStyle, width: 90, textAlign: "right" }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 0 }}>
                    <AssessmentEmptyState
                      title={`No ${mode} assessments`}
                      message="Try adjusting the filters or search term."
                      hasFilters={
                        Boolean(search) ||
                        Boolean(dateRange.from || dateRange.to)
                      }
                      onClearFilters={() => {
                        setSearch("");
                        setDateRange({ from: "", to: "" });
                      }}
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <Link to={`/assessment/assignment/${r.id}`}>
                        {r.assessment_title ||
                          `Assessment #${r.assessment_id}`}
                      </Link>
                    </td>
                    <td>{r.assigned_at?.slice(0, 10)}</td>
                    <td>{r.due_at ? r.due_at.slice(0, 10) : "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <Link
                        to={`/assessment/assignment/${r.id}`}
                        style={{
                          ...secondaryBtn,
                          height: 28,
                          padding: "0 10px",
                          fontSize: 12,
                          display: "inline-block",
                          lineHeight: "26px",
                          textDecoration: "none",
                        }}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SupplierManagementShell>
  );
};

export default MyAssignmentsByCompany;
